import throttle from 'lodash/throttle';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {checkIfDialogVisible} from '../../../../../../common/checkIfDialogVisible';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import {checkIfImagePopupVisible} from '../../../../../../imagePopup/checkIfImagePopupVisible';
import {TEXT_VIEWER_SCROLL_THRESHOLD} from '../constant';
import useScrollViewerWheelEvent from '../hook/useScrollViewerWheelEvent';
import {goToPrevChapter} from '../state/textViewerSlice';

export function ScrollTextViewerTopContainer({productId}: {productId: number}) {
  const touchStartPositisionY = useRef(0);

  const [reachedStart, setReachedStart] = useState(false);
  const reachedStartRef = useRef(false);

  const dispatch = useTypedDispatch();

  const onReleaseHandler = useCallback(
    (event: TouchEvent) => {
      const touchEndPositisionY = event.changedTouches[0].clientY;
      if (
        touchStartPositisionY.current <
          touchEndPositisionY - TEXT_VIEWER_SCROLL_THRESHOLD &&
        !checkIfDialogVisible() &&
        !checkIfImagePopupVisible()
      ) {
        dispatch(goToPrevChapter({productId}));
      }
    },
    [dispatch, productId]
  );

  const onPressHandler = useCallback((event: TouchEvent) => {
    touchStartPositisionY.current = event.touches[0].clientY;
  }, []);

  const onWheelHandler = useMemo(
    () =>
      throttle(
        (event: WheelEvent) => {
          if (
            event.deltaY < 0 &&
            !checkIfDialogVisible() &&
            !checkIfImagePopupVisible()
          ) {
            dispatch(goToPrevChapter({productId}));
          }
        },
        500,
        {
          leading: false,
          trailing: true,
        }
      ),
    [dispatch, productId]
  );

  const onKeyDownHandler = useCallback(
    (event: KeyboardEvent) => {
      if (
        (event.code === 'ArrowUp' || event.code === 'Up') &&
        !checkIfDialogVisible() &&
        !checkIfImagePopupVisible()
      ) {
        dispatch(goToPrevChapter({productId}));
      }
    },
    [dispatch, productId]
  );

  const handleScroll = useMemo(
    () =>
      throttle(() => {
        // 스크롤이 가장 위로 올라온 경우, reachedStart 값을 true로 변경합니다.
        if (Math.floor(window.pageYOffset - 1) <= 0) {
          if (!reachedStartRef.current) {
            setReachedStart(true);
          }
        } else {
          if (reachedStartRef.current) {
            setReachedStart(false);
          }
        }
      }, 100),
    []
  );

  useEffect(() => {
    reachedStartRef.current = reachedStart;
  }, [reachedStart]);

  useEffect(() => {
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  useScrollViewerWheelEvent({
    onPressHandler,
    onReleaseHandler,
    onScroll: handleScroll,
    onWheelHandler,
    onKeyDownHandler,
    isReached: reachedStart,
    isReachedRef: reachedStartRef,
  });

  return null;
}
